import type {TableColumnsType as ColumnsType} from "antd";
import IntlMessages from "src/domain/utility/IntlMessages";
import { dateFormat } from "src/shared/functions/date-format";
import {
  formatDate,
  TreatmentOrderStatus,
  UserType,
} from "src/shared/constants/AppConst";
import { NotFound } from "src/shared/components/icons";
import TooltipText from "src/shared/functions/tooltip-text";
import { TreatmentTypeValues } from "src/shared/constants/AppEnums";
import { TreatmentDetailsDto } from "src/shared/services/patient/dto/treatment-details-dto";
import { AdditionalKindLabel, CoveredByPlanTag } from "../treatment-row";
import { Fragment } from "react";
import { Button, Col, Row, Tooltip } from "antd";
import { AiOutlineInfoCircle } from "react-icons/ai";
import { useNavigate } from "react-router-dom";
import { useIntl } from "react-intl";
import hasPermission from "src/shared/functions/access-control";
import { ManageTreatments } from "src/shared/constants/permissions-enum";
import AccessControlComponent from "src/shared/components/access-control";
import MainUtils from "src/shared/utils/main";
import { GrPlan } from "react-icons/gr";
import { useSelector } from "react-redux";
import { getMyProfile } from "../../../domain/app/redux/auth/auth-selectors";
import type {TableColumnType as ColumnType} from "antd";

const TableColumn = (): ColumnsType<any> => {
  const { messages } = useIntl();
  const navigate = useNavigate();
  const myProfile = useSelector(getMyProfile);

  const goToTreatmentDetailsPage = (item: TreatmentDetailsDto) => {
    if (MainUtils.isEmptyValue(item?.isAdditional)) {
      navigate(`/treatments/details/${item.id}`);
    } else navigate(`/treatments/details/${item?.patientId}/${item?.id}`);
  };

  const goToPlanDetailsPage = (
    treatmenttId: string,
    additionalTreatmentId?: string,
  ) => {
    if (MainUtils.isEmptyValue(additionalTreatmentId))
      navigate(`/treatments/plan-details/${treatmenttId}`);
    else
      navigate(
        `/treatments/plan-details/${treatmenttId}/${additionalTreatmentId}`,
      );
  };

  return [
    {
      title: <IntlMessages id="common.id" />,
      dataIndex: ["patient", "referenceNumber"],
      align: "center",
      key: "id",
      width: "20%",
      render: (item) => {
        return TooltipText(item, 40) ?? <NotFound />;
      },
    },
    ...(myProfile?.userType !== UserType.Planner
      ? [
          {
            title: <IntlMessages id="common.patientName" />,
            dataIndex: ["patient", "fullName"],
            align: "center" as ColumnType<any>["align"],
            key: "name",
            width: "20%",
            render: (item: any) => {
              return TooltipText(item, 40) ?? <NotFound />;
            },
          },
        ]
      : []),
    {
      title: <IntlMessages id="common.treatmentType" />,
      dataIndex: "treatmentType",
      key: "treatmentType",
      align: "center" as ColumnType<any>["align"],
      width: "15%",
      render: (item: any, record: TreatmentDetailsDto) => {
        const typeLabel = (() => {
          switch (item) {
            case TreatmentTypeValues?.Aligner:
              return <IntlMessages id="common.aligner" />;
            case TreatmentTypeValues?.Whitening:
              return <IntlMessages id="common.whitening" />;
            case TreatmentTypeValues?.Retainers:
              return <IntlMessages id="common.retainers" />;
            default:
              return <NotFound />;
          }
        })();
        return (
          <>
            {typeLabel}
            <AdditionalKindLabel record={record} />
          </>
        );
      },
    },
    ...(myProfile?.userType !== UserType.Planner
      ? [
          {
            title: <IntlMessages id="common.doctorName" />,
            dataIndex: "doctor",
            key: "doctor",
            align: "center" as ColumnType<any>["align"],
            width: "15%",
            render: (item: { firstName: any; lastName: any }) => {
              return (
                TooltipText(`${item?.firstName} ${item?.lastName}`, 25) ?? (
                  <NotFound />
                )
              );
            },
          },
        ]
      : []),
    {
      title: <IntlMessages id="common.packageType" />,
      dataIndex: ["package", "displayName"],
      key: "treatmentPackage",
      align: "center" as ColumnType<any>["align"],
      width: "15%",
      render: (item: any, record: TreatmentDetailsDto) => (
        <>
          {TooltipText(item, 40) ?? <NotFound />}
          <CoveredByPlanTag record={record} />
        </>
      ),
    },
    {
      title: <IntlMessages id="common.createdOn" />,
      dataIndex: "dateCreated",
      key: "dateCreated",
      align: "center",
      width: "15%",
      render: (item) => {
        return dateFormat(item, formatDate);
      },
    },
    {
      title: <IntlMessages id="common.updatedOn" />,
      dataIndex: "dateUpdated",
      key: "dateUpdated",
      align: "center",
      width: "15%",
      render: (item) => {
        return dateFormat(item, formatDate);
      },
    },
    {
      title: <IntlMessages id="common.orderStatus" />,
      dataIndex: "treatmentOrderStatus",
      key: "treatmentOrderStatus",
      align: "center",
      width: "15%",
      render: (item) => {
        let status = "";
        Object.entries(TreatmentOrderStatus).map(([key, value]) => {
          if (value === item) {
            status = `common.${key}`;
          }
          return [key, value];
        });
        if (status) return messages[status].toString();
        else return <NotFound />;
      },
    },
    {
      title: <IntlMessages id="common.action" />,
      render: (text: string, item: TreatmentDetailsDto) => (
        <Fragment>
          {hasPermission(ManageTreatments.Details) ? (
            <Row justify="center" align="middle" gutter={[10, 10]}>
              <AccessControlComponent permission={ManageTreatments.Details}>
                <Col>
                  <Tooltip title={<IntlMessages id="common.details" />}>
                    <Button
                      type="link"
                      onClick={() => {
                        goToTreatmentDetailsPage(item);
                      }}
                      key={"details"}
                      className="no-padding"
                    >
                      <AiOutlineInfoCircle className="icon-in-action" />
                    </Button>
                  </Tooltip>
                </Col>
              </AccessControlComponent>
              {Number(item?.treatmentOrderStatus) ===
                Number(TreatmentOrderStatus.revision) && (
                <AccessControlComponent permission={ManageTreatments.Details}>
                  <Col>
                    <Tooltip
                      title={<IntlMessages id="details.plan.planDetails" />}
                    >
                      <Button
                        type="link"
                        onClick={() => {
                          if (item?.isAdditional) {
                            goToPlanDetailsPage(item.patientId ?? "", item.id);
                          } else goToPlanDetailsPage(item.id ?? "");
                        }}
                        key={"details"}
                        className="no-padding"
                      >
                        <GrPlan className="icon-in-action" />
                      </Button>
                    </Tooltip>
                  </Col>
                </AccessControlComponent>
              )}
            </Row>
          ) : (
            <NotFound />
          )}
        </Fragment>
      ),
      align: "center",
      width: "10%",
    },
  ];
};

export default TableColumn;
